import type { BackendContext, UISpec } from '../common/types.js';
import type { GengageIndexedDB } from '../common/indexed-db.js';
import type { ChatMessage } from './types.js';
import type { PanelSource } from './panel-manager.js';

const STORAGE_VERSION = 1;
// Legacy localStorage keys — only read once during migration into IndexedDB.
const INDEX_KEY_PREFIX = 'gengage:chat-history:index';
const SESSION_KEY_PREFIX = 'gengage:chat-history:session';
const MAX_HISTORY_SESSIONS = 12;
const ATTACHMENT_PLACEHOLDER = 'Image attachment';

type LegacyThumbnailEntry = { sku: string; imageUrl: string; threadId: string };

interface StoredHistoryMessage {
  id: string;
  threadId?: string | undefined;
  role: 'user' | 'assistant';
  content?: string | undefined;
  uiSpec?: UISpec | undefined;
  frontendOnly?: boolean | undefined;
  silent?: boolean | undefined;
  renderHint?: string | undefined;
  photoAnalysis?:
    | {
        summary: string;
        strengths?: string[];
        focusPoints?: string[];
        celebStyle?: string;
        celebStyleReason?: string;
        nextQuestion?: string;
      }
    | undefined;
  timestamp: number;
  status: 'done' | 'error';
}

export interface ChatHistoryIndexEntry {
  id: string;
  title: string;
  preview: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  pageType?: string | undefined;
  sku?: string | undefined;
  sourceSessionId: string;
}

export interface ChatHistorySessionRecord extends ChatHistoryIndexEntry {
  version: number;
  userId: string;
  appId: string;
  messages: StoredHistoryMessage[];
  currentThreadId: string | null;
  lastThreadId: string | null;
  panelThreads?: string[] | undefined;
  /** Legacy thumbnail-strip data kept optional for backward compatibility with older local history. */
  thumbnailEntries?: LegacyThumbnailEntry[] | undefined;
  panelSnapshotHtml?: Record<string, string> | undefined;
  panelSnapshotTypes?: Record<string, string> | undefined;
  panelSnapshotSources?: Record<string, PanelSource> | undefined;
  panelAiSpecByThread?: Record<string, UISpec> | undefined;
  lastBackendContext: BackendContext | null;
}

export interface SaveHistorySessionParams {
  historyId: string;
  userId: string;
  appId: string;
  sourceSessionId: string;
  messages: ChatMessage[];
  currentThreadId: string | null;
  lastThreadId: string | null;
  chatCreatedAt: string;
  panelSnapshots: Map<string, HTMLElement>;
  panelSnapshotTypes: Map<string, string>;
  panelSnapshotSources: ReadonlyMap<string, PanelSource>;
  panelThreads: string[];
  panelAiSpecByThread?: Map<string, UISpec> | undefined;
  lastBackendContext: BackendContext | null;
  pageType?: string | undefined;
  sku?: string | undefined;
  attachmentPlaceholder?: string | undefined;
}

function truncate(value: string, max: number): string {
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

function stripHtmlToPlainText(value: string): string {
  const normalized = value.trim();
  if (normalized.length === 0) return '';

  if (typeof DOMParser !== 'undefined') {
    // Parse into an inert document instead of assigning innerHTML on a live
    // element: a live node would eagerly fetch resources (e.g. <img src> /
    // onerror) just to extract text. DOMParser builds a detached document that
    // never triggers network requests or script execution.
    const body = new DOMParser().parseFromString(normalized, 'text/html').body;
    return (body.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  return normalized
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function safeParseJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function createScope(appId: string, userId: string): string {
  return `${encodeURIComponent(appId)}::${encodeURIComponent(userId || '__guest__')}`;
}

function indexKey(scope: string): string {
  return `${INDEX_KEY_PREFIX}:${scope}`;
}

function sessionKey(scope: string, historyId: string): string {
  return `${SESSION_KEY_PREFIX}:${scope}:${historyId}`;
}

function isMeaningfulMessage(message: ChatMessage | StoredHistoryMessage): boolean {
  if (message.silent) return false;
  const text = typeof message.content === 'string' ? stripHtmlToPlainText(message.content) : '';
  const hasAttachment = 'attachment' in message && message.attachment !== undefined;
  return text.length > 0 || message.uiSpec !== undefined || hasAttachment;
}

function serializeMessages(
  messages: ChatMessage[],
  attachmentPlaceholder = ATTACHMENT_PLACEHOLDER,
): StoredHistoryMessage[] {
  return messages
    .filter((message) => {
      if (message.status === 'streaming' && !isMeaningfulMessage(message)) return false;
      return true;
    })
    .map((message) => {
      const serialized: StoredHistoryMessage = {
        id: message.id,
        role: message.role,
        timestamp: message.timestamp,
        status: message.status === 'error' ? 'error' : 'done',
      };
      if (message.threadId !== undefined) serialized.threadId = message.threadId;
      if (message.content !== undefined) serialized.content = message.content;
      if (message.attachment !== undefined && stripHtmlToPlainText(serialized.content ?? '').length === 0) {
        serialized.content = attachmentPlaceholder;
      }
      if (message.uiSpec !== undefined) serialized.uiSpec = message.uiSpec;
      if (message.frontendOnly) serialized.frontendOnly = true;
      if (message.silent) serialized.silent = true;
      if (message.renderHint !== undefined) serialized.renderHint = message.renderHint;
      if (message.photoAnalysis !== undefined) serialized.photoAnalysis = message.photoAnalysis;
      return serialized;
    });
}

function deriveTitle(messages: StoredHistoryMessage[]): string {
  const firstUser = messages.find((message) => {
    return (
      message.role === 'user' &&
      !message.silent &&
      typeof message.content === 'string' &&
      stripHtmlToPlainText(message.content).length > 0
    );
  });
  if (firstUser?.content) return truncate(stripHtmlToPlainText(firstUser.content), 72);

  const firstVisible = messages.find((message) => isMeaningfulMessage(message));
  if (firstVisible?.content) return truncate(stripHtmlToPlainText(firstVisible.content), 72);

  return 'New chat';
}

function derivePreview(messages: StoredHistoryMessage[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message || message.silent) continue;
    if (typeof message.content === 'string' && message.content.trim().length > 0) {
      const plainText = stripHtmlToPlainText(message.content);
      if (plainText.length > 0) return truncate(plainText, 140);
    }
    if (message.uiSpec) {
      const root = message.uiSpec.elements[message.uiSpec.root];
      if (root?.type === 'PanelRestoreCard') {
        const props = (root.props ?? {}) as Record<string, unknown>;
        const ctaLabel = typeof props['ctaLabel'] === 'string' ? props['ctaLabel'].trim() : '';
        if (ctaLabel.length > 0) return truncate(ctaLabel, 140);
      }
      if (root?.type) return truncate(root.type, 140);
    }
  }
  return '';
}

function visibleMessageCount(messages: StoredHistoryMessage[]): number {
  return messages.filter((message) => !message.silent).length;
}

function normalizeIndexEntry(entry: ChatHistoryIndexEntry): ChatHistoryIndexEntry {
  return {
    ...entry,
    title: truncate(stripHtmlToPlainText(entry.title), 72) || 'New chat',
    preview: truncate(stripHtmlToPlainText(entry.preview), 140),
  };
}

function normalizeRecord(record: ChatHistorySessionRecord): ChatHistorySessionRecord {
  return {
    ...record,
    title: truncate(stripHtmlToPlainText(record.title), 72) || 'New chat',
    preview: truncate(stripHtmlToPlainText(record.preview), 140),
  };
}

function toIndexEntry(record: ChatHistorySessionRecord): ChatHistoryIndexEntry {
  return normalizeIndexEntry({
    id: record.id,
    title: record.title,
    preview: record.preview,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
    messageCount: record.messageCount,
    pageType: record.pageType,
    sku: record.sku,
    sourceSessionId: record.sourceSessionId,
  });
}

/**
 * The chat-history archive (past chat sessions).
 *
 * Backed by IndexedDB so bulk history is NOT kept in the origin-shared
 * localStorage. A lightweight index is hydrated into memory at init so the
 * history panel can list sessions synchronously; full records are read lazily
 * (async `loadSession`) and writes go through to IndexedDB behind a small mutex.
 * Existing localStorage history is migrated into IndexedDB once, on `hydrate()`.
 */
export class ChatHistoryStorage {
  private readonly _scope: string;
  /** Lightweight index, newest-first; the synchronous read surface. */
  private _index: ChatHistoryIndexEntry[] = [];
  private _hydrated = false;
  /** Serializes write-through so concurrent saves/deletes don't race. */
  private _writeChain: Promise<void> = Promise.resolve();

  constructor(
    private readonly _appId: string,
    private readonly _userId: string,
    private readonly _db: GengageIndexedDB | null,
    private readonly _localStorage: Storage | null = typeof window !== 'undefined' ? window.localStorage : null,
  ) {
    this._scope = createScope(_appId, _userId);
  }

  /**
   * Migrate any legacy localStorage history into IndexedDB, then load the index
   * into memory. Idempotent; safe to await once after the DB is open.
   */
  async hydrate(): Promise<void> {
    if (this._hydrated) return;
    this._hydrated = true;
    if (!this._db) return;
    try {
      await this._migrateFromLocalStorage();
      const records = await this._db.loadHistoryByScope<ChatHistorySessionRecord>(this._appId, this._userId);
      this._index = records
        .filter((r) => r && r.version === STORAGE_VERSION && Array.isArray(r.messages))
        .map(toIndexEntry)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .slice(0, MAX_HISTORY_SESSIONS);
    } catch {
      this._index = [];
    }
  }

  createHistoryId(): string {
    return crypto.randomUUID();
  }

  findSessionId(sourceSessionId: string, createdAt: string): string | null {
    if (!sourceSessionId || !createdAt) return null;
    const match = this._index.find(
      (entry) => entry.sourceSessionId === sourceSessionId && entry.createdAt === createdAt,
    );
    return match?.id ?? null;
  }

  listSessions(): ChatHistoryIndexEntry[] {
    return [...this._index];
  }

  /** Resolves once all queued write-through operations have settled. */
  flush(): Promise<void> {
    return this._writeChain;
  }

  async loadSession(historyId: string): Promise<ChatHistorySessionRecord | null> {
    if (!this._db) return null;
    // Read-after-write consistency: a record just handed to saveSession() is
    // written asynchronously, so drain the write queue before reading it back.
    await this._writeChain.catch(() => undefined);
    let record: ChatHistorySessionRecord | null = null;
    try {
      record = await this._db.loadHistory<ChatHistorySessionRecord>(this._appId, this._userId, historyId);
    } catch {
      return null;
    }
    if (!record || record.version !== STORAGE_VERSION) return null;
    // A corrupt record (e.g. messages mangled to a non-array) must degrade to
    // no-history rather than crash the restore path downstream.
    if (!Array.isArray(record.messages)) return null;
    return normalizeRecord(record);
  }

  /**
   * Build and persist a history record. The record is built synchronously (it
   * reads live panel DOM) and the in-memory index is updated synchronously so
   * callers see it immediately; the IndexedDB write happens asynchronously
   * behind the write mutex. Returns the built record, or null when there is
   * nothing worth saving / no backing store.
   */
  saveSession(params: SaveHistorySessionParams): ChatHistorySessionRecord | null {
    if (!this._db) return null;

    const messages = serializeMessages(params.messages, params.attachmentPlaceholder);
    const hasVisibleUserMessage = messages.some((message) => {
      return (
        message.role === 'user' &&
        !message.silent &&
        typeof message.content === 'string' &&
        stripHtmlToPlainText(message.content).length > 0
      );
    });
    if (!hasVisibleUserMessage) return null;

    const updatedAt = new Date().toISOString();
    const title = deriveTitle(messages);
    const preview = derivePreview(messages);

    const panelSnapshotHtml: Record<string, string> = {};
    for (const [messageId, element] of params.panelSnapshots) {
      if (element.querySelector('.gengage-chat-panel-skeleton')) continue;
      panelSnapshotHtml[messageId] = element.innerHTML;
    }

    const panelSnapshotTypes: Record<string, string> = {};
    for (const [messageId, type] of params.panelSnapshotTypes) {
      panelSnapshotTypes[messageId] = type;
    }
    const panelSnapshotSources: Record<string, PanelSource> = {};
    for (const [messageId, source] of params.panelSnapshotSources) {
      panelSnapshotSources[messageId] = source;
    }
    const panelAiSpecByThread: Record<string, UISpec> = {};
    for (const [threadId, spec] of params.panelAiSpecByThread ?? []) {
      panelAiSpecByThread[threadId] = spec;
    }

    const record: ChatHistorySessionRecord = {
      version: STORAGE_VERSION,
      id: params.historyId,
      userId: params.userId,
      appId: params.appId,
      sourceSessionId: params.sourceSessionId,
      title,
      preview,
      createdAt: params.chatCreatedAt,
      updatedAt,
      messageCount: visibleMessageCount(messages),
      pageType: params.pageType,
      sku: params.sku,
      messages,
      currentThreadId: params.currentThreadId,
      lastThreadId: params.lastThreadId,
      panelThreads: params.panelThreads.length > 0 ? params.panelThreads : undefined,
      panelSnapshotHtml: Object.keys(panelSnapshotHtml).length > 0 ? panelSnapshotHtml : undefined,
      panelSnapshotTypes: Object.keys(panelSnapshotTypes).length > 0 ? panelSnapshotTypes : undefined,
      panelSnapshotSources: Object.keys(panelSnapshotSources).length > 0 ? panelSnapshotSources : undefined,
      panelAiSpecByThread: Object.keys(panelAiSpecByThread).length > 0 ? panelAiSpecByThread : undefined,
      lastBackendContext: params.lastBackendContext,
    };

    // Update the in-memory index (newest-first), then cap to MAX_HISTORY_SESSIONS.
    this._index = [toIndexEntry(record), ...this._index.filter((entry) => entry.id !== record.id)].sort((a, b) =>
      b.updatedAt.localeCompare(a.updatedAt),
    );
    const evicted: string[] = [];
    while (this._index.length > MAX_HISTORY_SESSIONS) {
      const removed = this._index.pop();
      if (removed) evicted.push(removed.id);
    }

    this._enqueueWrite(async (db) => {
      await db.saveHistory(record);
      for (const id of evicted) {
        await db.deleteHistory(this._appId, this._userId, id);
      }
    });

    return record;
  }

  deleteSession(historyId: string): void {
    this._index = this._index.filter((entry) => entry.id !== historyId);
    this._enqueueWrite((db) => db.deleteHistory(this._appId, this._userId, historyId));
  }

  /** Queue a best-effort IndexedDB write; failures are swallowed (persistence is best-effort). */
  private _enqueueWrite(fn: (db: GengageIndexedDB) => Promise<void>): void {
    const db = this._db;
    if (!db) return;
    this._writeChain = this._writeChain.then(() => fn(db)).catch(() => undefined);
  }

  private async _migrateFromLocalStorage(): Promise<void> {
    if (!this._db || !this._localStorage) return;
    const rawIndex = this._localStorage.getItem(indexKey(this._scope));
    if (rawIndex === null) return;
    const oldIndex = safeParseJson<ChatHistoryIndexEntry[]>(rawIndex) ?? [];
    for (const entry of oldIndex) {
      const record = safeParseJson<ChatHistorySessionRecord>(
        this._localStorage.getItem(sessionKey(this._scope, entry.id)),
      );
      if (record && record.version === STORAGE_VERSION && Array.isArray(record.messages)) {
        try {
          await this._db.saveHistory(record);
        } catch {
          // Best-effort migration: a single bad record must not block the rest.
        }
      }
      this._localStorage.removeItem(sessionKey(this._scope, entry.id));
    }
    // Drop the legacy index last, so an interrupted migration retries next load.
    this._localStorage.removeItem(indexKey(this._scope));
  }
}
